import React from "react";

export function toFixed(numStr: string, prec: number): string {
  const num = Number(numStr);
  if (Number.isNaN(num)) return numStr;

  return num.toFixed(prec);
}

export function formatWithPrecision(
  value: string | number | undefined,
  precision: number
): string | undefined {
  if (precision < 0 || !value) return value as string | undefined;

  const str = value.toString();
  const trimmed = str.trim();
  if (trimmed !== "" && !Number.isNaN(Number(trimmed)))
    return toFixed(str, precision);

  return str;
}

export function enforceDecimalPrecision(
  value: string,
  precision: number
): string {
  if (precision < 0 || !value) return value;

  const valueStr = value.toString();
  if (precision === 0) return valueStr.split(".")[0];

  const regex = new RegExp(`^\\d*\\.?\\d{0,${precision}}$`);
  if (regex.test(valueStr)) return value;

  const parts = valueStr.split(".");
  if (parts.length === 1) return parts[0];

  return `${parts[0]}.${parts[1].substring(0, precision)}`;
}

export function formatWithRejectCharsRegex(
  value: string,
  rejectCharsRegex?: RegExp
): string {
  if (!rejectCharsRegex) return value;

  const globalRegex = new RegExp(rejectCharsRegex, "g");

  return value.replace(globalRegex, "");
}

export function getTrimmedValue(
  value: string | number,
  disableTrimOnBlur: boolean
): string | number {
  if (disableTrimOnBlur || typeof value !== "string") return value;

  return value.trim();
}

export function preserveCursor(
  e: React.ChangeEvent<HTMLInputElement>,
  updateValueFn: () => React.ChangeEvent<HTMLInputElement>
) {
  const input = e.target;
  const prevCursor = input.selectionStart;
  const prevValue = input.value;

  const updatedEvent = updateValueFn();

  const lengthDiff = input.value.length - prevValue.length;
  const newCursor = Math.max(0, (prevCursor ?? 0) + lengthDiff);

  const restore = () => {
    if (document.activeElement !== input) return;
    if (input.type === "number") return;

    input.setSelectionRange(newCursor, newCursor);
  };

  // Schedule via microtask (fires before paint, before any other event handlers run)
  // and fall back to RAF only if the microtask didn't run for some reason.
  // Leaving both armed races stale RAFs against subsequent keystrokes' onChange,
  // which corrupts cursor position under fast typing (jsdom + user-event).
  const rafId = requestAnimationFrame(restore);
  queueMicrotask(() => {
    cancelAnimationFrame(rafId);
    restore();
  });

  return updatedEvent;
}
